import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/currencyFormatter";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { analyticsEvents, trackEvent } from "@/services/analytics";

// Define form schema using Zod
const inventorySchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  description: z.string().optional(),
  currentQuantity: z.coerce
    .number()
    .nonnegative({ message: "Current quantity must be a positive number" }),
  unitOfMeasure: z.string().min(1, { message: "Unit is required" }),
  minimumQuantity: z.coerce
    .number()
    .nonnegative({ message: "Minimum quantity must be a positive number" }),
  expirationDate: z.date().optional().nullable(),
  reorderPoint: z.coerce
    .number()
    .nonnegative({ message: "Reorder point must be a positive number" })
    .optional(),
  category: z.string().optional(),
});

type InventoryFormValues = z.infer<typeof inventorySchema>;

interface EditInventoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inventoryItem: {
    id: string;
    name: string;
    description?: string;
    current_quantity: number;
    minimum_quantity: number;
    unit_of_measure: string;
    expiration_date?: string;
    expiry_date?: string;
    reorder_point?: number;
    category?: string;
    store_id?: string;
  };
  onInventoryUpdated?: () => void;
}

export function EditInventoryDialog({
  open,
  onOpenChange,
  inventoryItem,
  onInventoryUpdated,
}: EditInventoryDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inventoryCategories, setInventoryCategories] = useState<string[]>([]);

  // Fetch existing inventory categories for the dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from("inventory_items")
          .select("description")
          .not("description", "is", null);

        if (error) throw error;

        // Extract unique categories
        const categories = Array.from(
          new Set(
            data.map((item) => item.description).filter(Boolean) as string[]
          )
        );

        setInventoryCategories(categories);
      } catch (error) {
        console.error("Error fetching inventory categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Parse expiration date for the form
  const parseDate = (dateStr?: string) => {
    if (!dateStr) return undefined;
    try {
      return new Date(dateStr);
    } catch (error) {
      return undefined;
    }
  };

  // Track dialog opening
  useEffect(() => {
    if (open && inventoryItem.id) {
      trackEvent("inventory_edit_dialog_opened", {
        item_id: inventoryItem.id,
        item_name: inventoryItem.name,
        store_id: inventoryItem.store_id,
      });
    }
  }, [open, inventoryItem]);

  // Set up form with values from the inventoryItem
  const form = useForm<InventoryFormValues>({
    resolver: zodResolver(inventorySchema),
    defaultValues: {
      name: inventoryItem.name,
      description: inventoryItem.description || "",
      currentQuantity: inventoryItem.current_quantity,
      unitOfMeasure: inventoryItem.unit_of_measure,
      minimumQuantity: inventoryItem.minimum_quantity,
      expirationDate: parseDate(
        inventoryItem.expiration_date || inventoryItem.expiry_date
      ),
      reorderPoint: inventoryItem.reorder_point || 0,
      category: inventoryItem.description || "",
    },
  });

  // Reset form when inventoryItem changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: inventoryItem.name,
        description: inventoryItem.description || "",
        currentQuantity: inventoryItem.current_quantity,
        unitOfMeasure: inventoryItem.unit_of_measure,
        minimumQuantity: inventoryItem.minimum_quantity,
        expirationDate: parseDate(
          inventoryItem.expiration_date || inventoryItem.expiry_date
        ),
        reorderPoint: inventoryItem.reorder_point || 0,
        category: inventoryItem.description || "",
      });
    }
  }, [form, inventoryItem, open]);

  const onSubmit = async (data: InventoryFormValues) => {
    setIsSubmitting(true);

    try {
      // Format the expiration date for storage
      const formattedExpirationDate = data.expirationDate
        ? data.expirationDate.toISOString().split("T")[0]
        : null;

      // Track the old quantity for analytics
      const oldQuantity = inventoryItem.current_quantity;

      // Update the inventory item
      const { data: updatedItem, error } = await supabase
        .from("inventory_items")
        .update({
          name: data.name,
          description: data.category || data.description,
          current_quantity: data.currentQuantity,
          minimum_quantity: data.minimumQuantity,
          unit_of_measure: data.unitOfMeasure,
          expiration_date: formattedExpirationDate,
          reorder_point: data.reorderPoint,
          updated_at: new Date().toISOString(),
        })
        .eq("id", inventoryItem.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating inventory item:", error);
        throw error;
      }

      // Track inventory item update
      analyticsEvents.inventory.itemUpdated(
        inventoryItem.id,
        data.name,
        inventoryItem.store_id || ""
      );

      // Track quantity update if it changed
      if (oldQuantity !== data.currentQuantity) {
        analyticsEvents.inventory.quantityUpdated(
          inventoryItem.id,
          data.name,
          oldQuantity,
          data.currentQuantity,
          inventoryItem.store_id || ""
        );

        // Track stock alerts if applicable
        if (data.currentQuantity <= data.minimumQuantity) {
          analyticsEvents.inventory.stockAlert(
            inventoryItem.id,
            data.name,
            data.currentQuantity,
            data.minimumQuantity,
            inventoryItem.store_id || ""
          );
        }
      }

      // Show success toast
      toast({
        title: "Inventory item updated",
        description: `${data.name} has been updated successfully.`,
      });

      // Close dialog
      onOpenChange(false);

      // Trigger refresh of inventory items
      if (onInventoryUpdated) {
        onInventoryUpdated();
      }
    } catch (error) {
      console.error("Error updating inventory item:", error);
      toast({
        title: "Error",
        description: "Failed to update inventory item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-xl">
            Edit Inventory Item
          </DialogTitle>
          <DialogDescription className="text-sm">
            Update the details for this inventory item.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Item Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter item name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <div className="flex space-x-2">
                      <Input
                        {...field}
                        list="inventory-categories"
                        placeholder="Enter or select a category"
                        className="flex-1"
                      />
                      <datalist id="inventory-categories">
                        {inventoryCategories.map((category, index) => (
                          <option key={index} value={category} />
                        ))}
                      </datalist>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="currentQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unitOfMeasure"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., kg, pcs" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="minimumQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="reorderPoint"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reorder Point</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        placeholder="0"
                        min="0"
                        step="1"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expirationDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expiration Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <Calendar className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                className="mr-2"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
